<template>
  <j-modal :title="title" :width="'1500px'" :visible="visible" :maskClosable="false" :forceRender="true"
    :style="modalStyle" fullscreen switchFullscreen @cancel="handleCancel" wrapClassName="ant-modal-cust-warp">
    <!-- 顶部工具栏 -->
    <div v-if="!designerVisible" class="top-toolbar">
      <div style="display:flex; align-items:center; gap:8px;">
        <a-button icon="setting" @click="handleTemplateSetting">模板设置</a-button>
        <a-button type="primary" icon="printer" @click="handlePrintPreview">打印</a-button>
        <a-button icon="file-pdf" @click="handleExportPdf">导出PDF</a-button>
      </div>
    </div>
    <template slot="footer">
      <a-button @click="handleCancel">关闭</a-button>
    </template>

    <!-- 预览模式 -->
    <a-spin v-if="!designerVisible" :spinning="loading" tip="正在加载模板...">
      <div class="hiprint-container">
        <div v-if="!templateJson && !loading" class="no-template">
          <a-empty description="暂无打印模板">
            <a-button type="primary" @click="handleTemplateSetting">去设置模板</a-button>
          </a-empty>
        </div>
        <div v-else class="preview-wrapper">
          <div ref="printContent" class="print-content"
            :style="{ transform: 'scale(' + (zoomLevel / 100) + ')', transformOrigin: 'top center' }"></div>
        </div>
      </div>
    </a-spin>

    <!-- 设计器模式 -->
    <div v-if="designerVisible" class="designer-wrapper">
      <EmbeddedDesigner ref="embeddedDesigner" :billType="billTypeCode" :billTypeName="billTypeName"
        :billNo="currentBillNo" :templateId="selectedTemplateId" :billData="printData"
        @close="onDesignerClose" @saved="onDesignerSaved" />
    </div>
  </j-modal>
</template>

<script>
import { getList, loadTemplateWithFallback, getCurrentSystemInfo } from '@/api/plugin/print-template'
import HiprintService from '@/services/hiprint-service'
import EmbeddedDesigner from '@/views/plugin/print-template/EmbeddedDesigner.vue'
import { moneyToShort } from '@/utils/moneyUtils'

// 资产单据号前缀 → 模板 billTypeCode（兼容旧格式 ZCBD-20260918001 与新格式 ZCBD00000002490）
const PREFIX_CODE_MAP = [
  { prefix: 'ZCBD', code: 'ZCBD' },
  { prefix: 'ZCWX', code: 'ZCWX' },
  { prefix: 'ZCDB', code: 'ZCDB' },
  { prefix: 'ZCJT', code: 'ZCJT' },
  { prefix: 'ZCCZ', code: 'ZCCZ' },
  { prefix: 'ZCBF', code: 'ZCCZ' },
  { prefix: 'ZG', code: 'ZGD' }
]
const CODE_DISPLAY_NAME = {
  'ZGD': '资产转固单', 'ZCBD': '资产变动单', 'ZCWX': '资产维修单', 'ZCDB': '资产调拨单',
  'ZCJT': '折旧计提单', 'ZCCZ': '资产处置报废单', 'ZCKP': '固定资产卡片', 'ZCBQ': '资产标签'
}

export default {
  name: 'AssetBillPrint',
  components: { EmbeddedDesigner },
  data() {
    return {
      title: '资产单据打印',
      visible: false,
      modalStyle: '',
      loading: false,
      billTypeName: '',
      billTypeCode: '',
      currentBillNo: '',
      templateJson: null,
      printData: null,
      templateList: [],
      selectedTemplateId: null,
      zoomLevel: 100,
      designerVisible: false
    }
  },
  methods: {
    /**
     * 显示打印窗口（数据由调用方直接传入，不依赖主系统单据接口）
     * @param {String} billNo 单据编号（资产卡片传资产编码）
     * @param {String} billTypeName 单据类型中文名（可选，缺省按编号前缀推断）
     * @param {Object} head 单据头/资产卡片数据
     * @param {Array} items 明细数据（单资产单据可传 []）
     */
    async show(billNo, billTypeName, head, items) {
      this.visible = true
      this.modalStyle = 'top:20px;height: 95%;'
      this.loading = true
      this.currentBillNo = billNo
      this.billTypeName = billTypeName || ''
      this.billTypeCode = this.resolveCode(billNo, billTypeName)
      if (!this.billTypeName) {
        this.billTypeName = CODE_DISPLAY_NAME[this.billTypeCode] || '资产单据'
      }
      this.title = this.billTypeName + '-打印'
      try {
        this.printData = await this.transformData(head || {}, items || [])
        await this.loadTemplateList()
        await this.loadAndRender()
      } catch (e) {
        console.error('[AssetBillPrint] 加载失败:', e)
        this.templateJson = this.getFallbackTemplate()
        this.$nextTick(() => this.renderTemplate())
      } finally {
        this.loading = false
      }
    },
    /** 按编号前缀解析 billTypeCode（长前缀优先，避免 ZG- 误匹配 ZCBD-） */
    resolveCode(billNo, billTypeName) {
      for (const name in CODE_DISPLAY_NAME) {
        if (billTypeName === CODE_DISPLAY_NAME[name]) return name
      }
      if (billTypeName === '资产卡片') return 'ZCKP'
      if (billTypeName === '资产标签') return 'ZCBQ'
      if (billNo) {
        for (const p of PREFIX_CODE_MAP) {
          if (billNo.indexOf(p.prefix) === 0) return p.code
        }
      }
      return 'ZGD'
    },
    /** 组装打印数据（对齐主应用 transformData 字段别名约定） */
    async transformData(head, items) {
      let company = {}
      try {
        const res = await getCurrentSystemInfo()
        if (res && res.code === 200) company = res.data || {}
      } catch (e) { /* 公司信息获取失败不影响打印 */ }
      const rows = (items || []).map((it, i) => ({
        ...it,
        materialName: it.materialName || it.assetName || '',
        categoryName: it.categoryName || '',
        qty: it.qty != null ? it.qty : 1,
        amount: it.amount != null ? it.amount
          : (it.depreciationAmount != null ? it.depreciationAmount
            : (it.changeAmount != null ? it.changeAmount : '')),
        rowIndex: i + 1
      }))
      // 单资产单据（变动/维修/调拨/处置报废/卡片）items 为空，用单据头构造一行明细
      if (!rows.length && (head.assetName || head.assetCode)) {
        rows.push({
          rowIndex: 1,
          assetCode: head.assetCode || '',
          assetName: head.assetName || '',
          materialName: head.assetName || '',
          categoryName: head.categoryName || '',
          assetModel: head.assetModel || '',
          changeType: head.changeType || '',
          repairType: head.repairType || '',
          disposalType: head.disposalType || '',
          qty: 1,
          amount: head.changeAmount != null ? head.changeAmount
            : (head.amount != null ? head.amount
              : (head.originalValue != null ? head.originalValue : ''))
        })
      }
      const totalQty = rows.reduce((s, it) => s + (Number(it.qty) || 0), 0)
      const totalAmount = rows.length
        ? rows.reduce((s, it) => s + (Number(it.amount) || 0), 0)
        : (Number(head.totalAmount) || Number(head.originalValue) || Number(head.changeAmount) || Number(head.amount) || 0)
      return {
        billTitle: this.billTypeName,
        number: this.currentBillNo,
        billNo: this.currentBillNo,
        operTimeStr: (head.billTime || head.createTime || '').substring(0, 10),
        billDate: (head.billTime || head.createTime || '').substring(0, 10),
        createTime: head.createTime || '',
        // 公司信息
        companyName: company.companyName || '',
        companyAddress: company.companyAddress || '',
        companyTel: company.companyTel || '',
        // 往来单位
        organName: head.organName || '',
        supplierName: head.organName || '',
        contacts: head.contacts || '',
        telephone: head.telephone || '',
        // 单据头字段透传（资产单据特有）
        ...head,
        // 明细（多别名兼容模板）
        detailList: rows,
        table: rows,
        itemList: rows,
        // 汇总
        totalQty: totalQty,
        totalAmount: totalAmount,
        billDetailAllPrice: totalAmount,
        billDetailCnMoney: moneyToShort(totalAmount),
        // 卡片/标签二维码取值（模板 qrcode 元素 field=qrCodeValue）
        qrCodeValue: head.assetCode || this.currentBillNo || ''
      }
    },
    async loadTemplateList() {
      if (!this.billTypeCode) return
      try {
        const res = await getList({ billType: this.billTypeCode, size: 100 })
        if (res.code === 200 && res.data) {
          this.templateList = res.data.list || res.data || []
          const def = this.templateList.find((t) => t.isDefault === 1 || t.isDefault === true)
          this.selectedTemplateId = def ? def.id : (this.templateList.length ? this.templateList[0].id : null)
        }
      } catch (e) {
        this.templateList = []
      }
    },
    async loadAndRender() {
      try {
        if (this.selectedTemplateId) {
          const tpl = this.templateList.find((t) => t.id === this.selectedTemplateId)
          if (tpl && tpl.templateJson) {
            this.templateJson = this.parseTemplateJson(tpl.templateJson)
          } else {
            await this.loadDefaultTemplate()
          }
        } else {
          await this.loadDefaultTemplate()
        }
        this.$nextTick(() => this.renderTemplate())
      } catch (e) {
        console.error('[AssetBillPrint] loadAndRender error:', e)
      }
    },
    async loadDefaultTemplate() {
      this.loading = true
      try {
        const userInfo = this.$store && (this.$store.getters.userInfo || (this.$store.state.user && this.$store.state.user.info))
        const tenantId = userInfo ? userInfo.tenantId : null
        const result = await loadTemplateWithFallback(this.billTypeCode, tenantId)
        this.templateJson = (result && result.templateJson && result.templateJson.panels) ? result.templateJson : this.getFallbackTemplate()
      } catch (e) {
        this.templateJson = this.getFallbackTemplate()
      } finally {
        this.loading = false
      }
    },
    parseTemplateJson(json) {
      if (!json) return null
      try {
        return typeof json === 'string' ? JSON.parse(json) : json
      } catch (e) {
        return null
      }
    },
    /** 内置兜底模板（通用资产单据版式） */
    getFallbackTemplate() {
      return {
        panels: [{
          index: 0, name: 1, width: 210, height: 297,
          printElements: [
            { tid: 'provider', options: { left: 60, top: 10, width: 90, height: 15, field: 'billTitle', title: 'billTitle', fontSize: 16, fontWeight: 700, textAlign: 'center', hideTitle: true } },
            { tid: 'provider', options: { left: 20, top: 30, width: 85, height: 8, field: 'number', title: '单据编号', fontSize: 10, textAlign: 'left' } },
            { tid: 'provider', options: { left: 115, top: 30, width: 75, height: 8, field: 'billDate', title: '单据日期', fontSize: 10, textAlign: 'left' } },
            { tid: 'provider', options: { left: 20, top: 42, width: 85, height: 8, field: 'organName', title: '供应商', fontSize: 10, textAlign: 'left' } },
            { tid: 'provider', options: { left: 115, top: 42, width: 75, height: 8, field: 'remark', title: '备注', fontSize: 10, textAlign: 'left' } },
            {
              tid: 'table', options: {
                left: 20, top: 56, width: 170, height: 60, field: 'table', title: '明细',
                columns: [
                  { title: '序号', field: 'rowIndex', width: 15, align: 'center' },
                  { title: '物资/资产', field: 'materialName', width: 45, align: 'left' },
                  { title: '类别', field: 'categoryName', width: 30, align: 'left' },
                  { title: '数量', field: 'qty', width: 20, align: 'right' },
                  { title: '金额', field: 'amount', width: 25, align: 'right' }
                ],
                headerHeight: 8, rowHeight: 8, showHeader: true
              }
            },
            { tid: 'provider', options: { left: 20, top: 122, width: 85, height: 8, field: 'totalQty', title: '合计数量', fontSize: 10, textAlign: 'left' } },
            { tid: 'provider', options: { left: 115, top: 122, width: 75, height: 8, field: 'totalAmount', title: '合计金额', fontSize: 10, textAlign: 'left' } }
          ]
        }]
      }
    },
    renderTemplate() {
      if (!this.templateJson || !this.$refs.printContent) return
      try {
        if (HiprintService.isAvailable()) {
          const html = HiprintService.renderHtml(this.templateJson, this.printData || {})
          if (html && html.trim()) {
            this.$refs.printContent.innerHTML = html
          } else {
            this.renderFallback()
          }
        } else {
          this.renderFallback()
        }
      } catch (e) {
        this.renderFallback()
      }
    },
    /** 降级渲染：简单 HTML 版式 */
    renderFallback() {
      if (!this.$refs.printContent || !this.printData) return
      const d = this.printData
      const rows = d.itemList || []
      this.$refs.printContent.innerHTML = `
        <div style="padding: 20px; font-family: SimSun;">
          <div style="text-align:center; margin-bottom:16px;">
            <h2 style="font-size:22px; margin:10px 0;">${d.companyName || ''}</h2>
            <h3 style="font-size:18px; margin:0; color:#1890ff;">${d.billTitle || '资产单据'}</h3>
          </div>
          <div style="margin-bottom:12px;">
            <span style="margin-right:20px;">单据编号：${d.billNo || ''}</span>
            <span style="margin-right:20px;">单据日期：${d.billDate || ''}</span>
            <span>供应商：${d.organName || ''}</span>
          </div>
          <table style="width:100%; border-collapse:collapse; margin-bottom:12px;">
            <thead><tr style="background:#f0f0f0;">
              <th style="border:1px solid #d9d9d9; padding:6px; width:40px;">序号</th>
              <th style="border:1px solid #d9d9d9; padding:6px;">物资/资产</th>
              <th style="border:1px solid #d9d9d9; padding:6px; width:90px;">类别</th>
              <th style="border:1px solid #d9d9d9; padding:6px; width:60px;">数量</th>
              <th style="border:1px solid #d9d9d9; padding:6px; width:80px;">金额</th>
            </tr></thead>
            <tbody>
              ${rows.map((r) => `
                <tr>
                  <td style="border:1px solid #d9d9d9; padding:6px; text-align:center;">${r.rowIndex || ''}</td>
                  <td style="border:1px solid #d9d9d9; padding:6px;">${r.materialName || r.assetName || ''}</td>
                  <td style="border:1px solid #d9d9d9; padding:6px;">${r.categoryName || ''}</td>
                  <td style="border:1px solid #d9d9d9; padding:6px; text-align:right;">${r.qty || ''}</td>
                  <td style="border:1px solid #d9d9d9; padding:6px; text-align:right;">${r.amount == null ? '' : r.amount}</td>
                </tr>`).join('')}
            </tbody>
          </table>
          <div style="margin-bottom:12px;">备注：${d.remark || ''}</div>
          <div style="display:flex; justify-content:space-between;">
            <div>合计金额：${d.totalAmount || 0}（${d.billDetailCnMoney || ''}）</div>
          </div>
        </div>`
    },
    /** 打印（新窗口 print） */
    handlePrintPreview() {
      const content = this.$refs.printContent
      if (!content || !content.innerHTML) {
        this.$message.warning('暂无内容可打印')
        return
      }
      const w = window.open('', '_blank')
      if (!w) {
        this.$message.error('无法打开打印窗口，请允许弹出窗口')
        return
      }
      w.document.write('<!DOCTYPE html><html><head><title>' + this.billTypeName + '-打印</title>' +
        '<style>body{margin:0;padding:20px;font-family:SimSun,\'Microsoft YaHei\',sans-serif;}</style></head><body>' +
        content.innerHTML + '</body></html>')
      w.document.close()
      w.focus()
      setTimeout(() => w.print(), 500)
    },
    handleExportPdf() {
      if (!this.templateJson) {
        this.$message.warning('暂无模板数据')
        return
      }
      try {
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype.$hiprint)
        if (!hiprint || !hiprint.PrintTemplate) {
          this.$message.error('打印引擎未就绪')
          return
        }
        const fileName = this.billTypeName + '_' + new Date().toISOString().slice(0, 10)
        new hiprint.PrintTemplate({ template: this.templateJson }).toPdf(this.printData || {}, fileName)
        this.$message.success('PDF 导出成功')
      } catch (e) {
        this.$message.error('PDF 导出失败')
      }
    },
    handleTemplateSetting() { this.designerVisible = true },
    onDesignerClose() { this.designerVisible = false },
    onDesignerSaved() {
      this.designerVisible = false
      this.$message.success('模板保存成功，正在刷新预览...')
      this.loadTemplateList().then(() => this.loadAndRender())
    },
    handleCancel() { this.close() },
    close() {
      this.templateJson = null
      this.printData = null
      this.selectedTemplateId = null
      this.designerVisible = false
      if (this.$refs.printContent) {
        this.$refs.printContent.innerHTML = ''
      }
      this.$emit('close')
      this.visible = false
      this.modalStyle = ''
    }
  }
}
</script>

<style scoped>
.top-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}
.hiprint-container {
  width: 100%;
  height: calc(100vh - 280px);
  overflow: auto;
  background: #f5f5f5;
  padding: 20px;
  box-sizing: border-box;
}
.preview-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 500px;
}
.print-content {
  background: white;
  min-width: 210mm;
  min-height: 297mm;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform-origin: top center;
}
.no-template {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}
.designer-wrapper {
  height: calc(100vh - 200px);
  min-height: 600px;
}
</style>
